// Import HTTP client
import {
  registerService,
  resendVerificationEmailService,
  loginService,
  verifyEmailService,
  forgotPasswordService,
  resetPasswordService,
  initiate2faService,
  complete2faService,
  verify2faService,
  googleRegisterService,
  googleLoginService,
  businessConfigurationService,
  completePtAccountService,
  refreshTokenService,
  changePasswordService,
  inviteNewUserService,
  verifyUserInviteService,
  getAllInvitedUsersService,
  deactivateUserService,
  activateUserService,
  cancelUserInviteService,
  deleteUserService,
  resendUserInviteService,
  updateUserRoleService,
  localTransferDetailsService,
  transferDetailsService,
  adminBusinessAccessService,
  verifyAdminBusinessAccessService,
  getBaseUrlService,
  getUserService,
  resendVerificationWithEmailService
} from '@/services/authService'
import { SortTimeHandler, SortNameHandler } from '@/utils/helpers'
import { USER_TOKEN } from '@/utils/constants'
import tokenMixin from '@/mixins/token'

/** mutation types */
const set_user = 'set_user'
const set_invited_user_info = 'set_invited_user_info'
const set_allInvitedUsers = 'set_allInvitedUsers'
const set_business = 'set_business'
const set_access_token = 'set_access_token'
const set_authDetails = 'set_authDetails'
const set_kyc_to_true = 'set_kyc_to_true'
const set_refresh_token = 'set_refresh_token'
const set_token_expiry = 'set_token_expiry'
const set_businessConfig = 'set_businessConfig'
const set_ptWidgetToken = 'set_ptWidgetToken'
const set_WalletAddresses = 'set_WalletAddresses'
const set_localTransferDetails = 'set_localTransferDetails'
const set_transferDetails = 'set_transferDetails'
const set_terminalConfig = 'set_terminalConfig'
const set_serviceOfferings = 'set_serviceOfferings'
const set_kycDetailsForm = 'set_kycDetailsForm'
const set_adminBusinessAccessDetails = 'set_adminBusinessAccessDetails'
const set_verifiedAdminBusinessAccessDetails =
  'set_verifiedAdminBusinessAccessDetails'
const set_keepMeLoggedIn = 'set_keepMeLoggedIn'
const set_baseUrl = 'set_baseUrl'
const set_baseTestUrl = 'set_baseTestUrl'
const sort_usersNameData = 'sort_usersNameData'
const sort_usersTimeData = 'sort_usersTimeData'
const set_transactionInitiator = 'set_transactionInitiator'

/** state */
const getDefaultState = () => {
  return {
    user: {},
    authDetails: {},
    accessToken: null,
    refreshToken: null,
    tokenExpiry: null,
    ptWidgetToken: null,
    business: {},
    newTokenDetails: {},
    businessConfig: {},
    WalletAddresses: null,
    invitedUserInfo: null,
    allInvitedUsers: null,
    localTransferDetails: null,
    terminalConfig: {},
    serviceOfferings: {},
    transferDetails: null,
    kycDetailsForm: {},
    transactionInitiator: {},
    adminBusinessAccessDetails: {
      accessKey: '',
      requestID: ''
    },
    verifiedAdminBusinessAccessDetails: {
      AccessToken: '',
      RefreshToken: '',
      RefreshTokenExpiry: ''
    },
    keepMeLoggedIn: false,
    testUrl: '',
    baseTestUrl: null
  }
}

const state = getDefaultState()
/** getters */
const getters = {
  user: () => state.user,
  authDetails: () => state.authDetails,
  invitedUserInfo: () => state.invitedUserInfo,
  allInvitedUsers: () => state.allInvitedUsers
}

/** mutations */
const mutations = {
  [set_user] (state, data) {
    state.user = data
  },
  [set_invited_user_info] (state, data) {
    state.invitedUserInfo = data
  },
  [set_allInvitedUsers] (state, data) {
    state.allInvitedUsers = data
  },
  [set_business] (state, data) {
    state.business = data
  },
  [set_access_token] (state, data) {
    state.accessToken = data
  },
  [set_refresh_token] (state, data) {
    state.refreshToken = data
  },
  [set_token_expiry] (state, data) {
    state.tokenExpiry = data
  },
  [set_ptWidgetToken] (state, data) {
    state.ptWidgetToken = data
  },
  [set_WalletAddresses] (state, data) {
    state.WalletAddresses = data
  },
  [set_authDetails] (state, data) {
    state.authDetails = data
  },
  [set_kyc_to_true] (state) {
    state.business.completedKyc = true
  },
  [set_businessConfig] (state, data) {
    state.businessConfig = data
  },
  [set_localTransferDetails] (state, data) {
    state.localTransferDetails = data
  },
  [set_terminalConfig] (state, data) {
    state.terminalConfig = data
  },
  [set_serviceOfferings] (state, data) {
    state.serviceOfferings = data
  },
  [set_transferDetails] (state, data) {
    state.transferDetails = data
  },
  [set_kycDetailsForm] (state, data) {
    state.kycDetailsForm = { ...data, ...state.kycDetailsForm }
  },
  [set_adminBusinessAccessDetails] (state, data) {
    state.adminBusinessAccessDetails = data
  },
  [set_verifiedAdminBusinessAccessDetails] (state, data) {
    state.verifiedAdminBusinessAccessDetails = data
  },
  [set_keepMeLoggedIn] (state, data) {
    state.keepMeLoggedIn = data
  },
  [set_baseUrl] (state, data) {
    state.testUrl = data
  },
  [set_baseTestUrl] (state, data) {
    state.baseTestUrl = data
  },
  [set_transactionInitiator] (state, data) {
    state.transactionInitiator = data
  },
  resetState (state) {
    Object.assign(state, getDefaultState())
  },
  [sort_usersNameData] (state, sortDirection) {
    state.allInvitedUsers.ActiveUsers = SortNameHandler(
      state.allInvitedUsers.ActiveUsers,
      'firstName',
      sortDirection
    )
  },
  [sort_usersTimeData] (state, sortDirection) {
    state.allInvitedUsers.ActiveUsers = SortTimeHandler(
      state.allInvitedUsers.ActiveUsers,
      'lastSeenAt',
      sortDirection
    )
  }
}

/** actions */
const actions = {
  resetAuthState ({ commit }) {
    commit('resetState')
  },
  register: ({ commit }, payload) => {
    return registerService(payload)
  },
  login: ({ commit }, payload) => {
    return loginService(payload).then((response) => {
      commit('set_authDetails', response.data.data)
      commit('set_keepMeLoggedIn', payload.keepMeLoggedIn)
    })
  },
  verifyEmail: ({ commit }, payload) => {
    return verifyEmailService(payload)
  },
  forgotPassword: ({ commit }, payload) => {
    return forgotPasswordService(payload)
  },
  resetPassword: ({ commit }, payload) => {
    return resetPasswordService(payload)
  },
  changePassword: ({ commit }, payload) => {
    return changePasswordService(payload)
  },
  resendVerificationEmail: ({ commit }, payload) => {
    return resendVerificationEmailService(payload)
  },
  initiate2fa: ({ commit }, payload) => {
    return initiate2faService(payload)
  },
  complete2fa: ({ commit }, payload) => {
    return complete2faService(payload)
  },
  resendVerificationWithEmail: ({ commit }, payload) => {
    return resendVerificationWithEmailService(payload)
  },
  verify2fa: ({ commit }, payload) => {
    return verify2faService(payload).then((response) => {
      commit('set_user', response.data.data.user)
      commit('set_token_expiry', response.data.data.accessTokenExpiry)
      commit('set_access_token', response.data.data.accessToken)
      commit('set_refresh_token', response.data.data.refreshToken)
      commit('set_business', response.data.data.business)
      localStorage.setItem(USER_TOKEN, response.data.data.accessToken)
      tokenMixin.methods.setToken(
        response.data.data.accessToken,
        response.data.data.accessTokenExpiry
      )
    })
  },
  setAdminTokens: ({ commit }, payload) => {
    commit('set_access_token', payload.accessToken)
    commit('set_user', payload.user)
    commit('set_token_expiry', payload.accessTokenExpiry)
    commit('set_refresh_token', payload.refreshToken)
    commit('set_business', payload.business)
    tokenMixin.methods.setToken(payload.accessToken, payload.accessTokenExpiry)
  },
  googleRegister: ({ commit }, payload) => {
    return googleRegisterService(payload)
  },
  setKycToTrue: ({ commit }) => {
    commit('set_kyc_to_true')
  },
  googleLogin: ({ commit }, payload) => {
    return googleLoginService(payload).then((response) => {
      commit('set_authDetails', response.data.data)
      commit('set_keepMeLoggedIn', payload.keepMeLoggedIn)
    })
  },
  businessConfiguration: ({ commit }, payload) => {
    return businessConfigurationService(payload).then((response) => {
      commit('set_businessConfig', response.data.data.config)
      commit('set_ptWidgetToken', response.data.data.ptWidgetToken)
      commit('set_terminalConfig', response.data.data.terminalConfig)
      commit('set_serviceOfferings', response.data.data.serviceOfferings)
      commit('set_WalletAddresses', response.data.data.walletAddress)
    })
  },
  localTransferDetails: ({ commit }) => {
    return localTransferDetailsService().then((response) => {
      commit('set_localTransferDetails', response.data.data)
    })
  },
  transferDetails: ({ commit }) => {
    return transferDetailsService().then((response) => {
      commit('set_transferDetails', response.data.data)
    })
  },
  completePtAccount: ({ commit }, payload) => {
    return completePtAccountService(payload)
  },
  refreshToken: ({ commit }, keepMeLoggedIn) => {
    return refreshTokenService(keepMeLoggedIn).then((response) => {
      commit('set_token_expiry', response.data.data.accessTokenExpiry)
      commit('set_access_token', response.data.data.accessToken)
      commit('set_refresh_token', response.data.data.refreshToken)
      localStorage.setItem(USER_TOKEN, response.data.data.accessToken)
      tokenMixin.methods.setToken(
        response.data.data.accessToken,
        response.data.data.accessTokenExpiry
      )
    })
  },
  inviteNewUser: ({ commit }, payload) => {
    return inviteNewUserService(payload)
  },
  updateUserRole: ({ commit }, payload) => {
    return updateUserRoleService(payload)
  },
  verifyUserInvite: ({ commit }, payload) => {
    return verifyUserInviteService(payload)
  },
  invitedUserInfo: (payload, invitedUserInfo) => {
    return payload.commit('set_invited_user_info', invitedUserInfo)
  },
  getAllInvitedUsers: ({ commit }, payload) => {
    return getAllInvitedUsersService(payload).then((response) => {
      commit('set_allInvitedUsers', response.data.data)
    })
  },
  deactivateUser: ({ commit }, payload) => {
    return deactivateUserService(payload)
  },
  deleteUser: ({ commit }, payload) => {
    return deleteUserService(payload)
  },
  activateUser: ({ commit }, payload) => {
    return activateUserService(payload)
  },
  cancelUserInvite: ({ commit }, payload) => {
    return cancelUserInviteService(payload).then((res) => {})
  },
  resendUserInvite: ({ commit }, payload) => {
    return resendUserInviteService(payload).then((res) => {})
  },
  setKycDetailsForm: ({ commit }, payload) => {
    commit('set_kycDetailsForm', payload)
  },
  adminBusinessAccess: ({ commit }, payload) => {
    return adminBusinessAccessService(payload).then((response) => {
      commit('set_adminBusinessAccessDetails', response.data.data)
    })
  },
  verifyAdminBusinessAccess: ({ commit }, payload) => {
    return verifyAdminBusinessAccessService(payload).then((response) => {
      commit('set_verifiedAdminBusinessAccessDetails', response.data.data)
    })
  },
  getTransactionInitiator: ({ commit }, payload) => {
    return getUserService(payload).then((response) => {
      commit('set_transactionInitiator', response.data.data)
    })
  },
  setBaseUrl: ({ commit }, payload) => {
    commit('set_baseUrl', payload)
  },
  getBaseUrl: ({ commit }, payload) => {
    return getBaseUrlService(payload).then((response) =>
      commit('set_baseTestUrl', response.data.data)
    )
  },
  sortTimeHandler: ({ commit }, sortDirection) => {
    commit('sort_usersTimeData', sortDirection)
  },
  sortNameHandler: ({ commit }, sortDirection) => {
    commit('sort_usersNameData', sortDirection)
  }
}
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
